import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  CrudActionButton,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import apiService from 'library/api-service';
import { Grid2 as Grid } from '@mui/material';
import { renderDataGridLocaleText } from 'utils';
import { useSingleRowSelectionGuard } from 'hooks';
import {
  selectEnvironmentTypes,
  selectEnvironmentTypesIsEmpty,
  selectEnvironmentTypesLoading,
} from 'store/features/environment-types/selector';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import environmentTypesActions from 'store/features/environment-types/actions';
import { CreateEnvironmentTypeDialog, UpdateEnvironmentTypeDialog } from './components';

const EnvironmentTypes = () => {
  // other hooks
  const intl = useIntl();
  const requireSingleRowSelection = useSingleRowSelectionGuard();

  // redux hooks
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectEnvironmentTypesLoading);
  const environmentTypes = useAppSelector(selectEnvironmentTypes);
  const isEmptyEnvironmentTypes = useAppSelector(selectEnvironmentTypesIsEmpty);

  // useState hooks
  const [openDeleteEnvironmentType, setOpenDeleteEnvironmentType] = useState(false);
  const [openCreateEnvironmentType, setOpenCreateEnvironmentType] = useState(false);
  const [openUpdateEnvironmentType, setOpenUpdateEnvironmentType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'environmentTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(environmentTypesActions.getEnvironmentTypes());
  };

  // sync helper functions
  const handleCloseDeleteEnvironmentType = () => setOpenDeleteEnvironmentType(false);
  const handleClickOpenDeleteEnvironmentType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenDeleteEnvironmentType(true);
  };

  const handleCloseCreateEnvironmentType = () => setOpenCreateEnvironmentType(false);
  const handleClickOpenCreateEnvironmentType = () => setOpenCreateEnvironmentType(true);

  const handleCloseUpdateEnvironmentType = () => setOpenUpdateEnvironmentType(false);
  const handleClickOpenUpdateEnvironmentType = () => {
    if (requireSingleRowSelection(rowSelectionModel) === null) return;

    // If the check has been passed successfully...
    setOpenUpdateEnvironmentType(true);
  };

  // useEffect hooks
  useEffect(() => {
    if (!environmentTypes.length && !isEmptyEnvironmentTypes) {
      dispatch(environmentTypesActions.getEnvironmentTypes());
    }
  }, [environmentTypes, isEmptyEnvironmentTypes, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-environment-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="create" onClick={handleClickOpenCreateEnvironmentType}>
              <FormattedMessage id="environmentTypes.createEnvironmentType" defaultMessage="Sunucu Tipi Oluştur" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-environment-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="update" onClick={handleClickOpenUpdateEnvironmentType}>
              <FormattedMessage id="environmentTypes.updateEnvironmentType" defaultMessage="Sunucu Tipi Düzenle" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-environment-type']}>
          <Grid size={{ xs: 12, sm: 6, lg: 2 }}>
            <CrudActionButton action="delete" onClick={handleClickOpenDeleteEnvironmentType}>
              <FormattedMessage id="environmentTypes.deleteEnvironmentType" defaultMessage="Sunucu Tipi Sil" />
            </CrudActionButton>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateEnvironmentTypeDialog
        open={openCreateEnvironmentType}
        handleClose={handleCloseCreateEnvironmentType}
        refresh={refresh}
      />
      <UpdateEnvironmentTypeDialog
        open={openUpdateEnvironmentType}
        environmentTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateEnvironmentType}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteEnvironmentType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteEnvironmentType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="environmentTypes.deleteEnvironmentType" defaultMessage="Sunucu Tipi Sil" />}
        subTitle={
          <FormattedMessage
            id="environmentTypes.deleteEnvironmentTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra sunucu tipi veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteEnvironmentType}
        successMessage={
          <FormattedMessage
            id="environmentTypes.deleteEnvironmentTypeCompleted"
            defaultMessage="Seçili sunucu tipini silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText(intl)}
          rows={environmentTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default EnvironmentTypes;
